/**
 * Cache Service for Dashboard
 * Handles version-based caching with automatic expiration
 */

import { STORES } from './indexedDBService';

const DB_NAME = 'ShepherdDB';
const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get database connection
 * @returns {Promise<IDBDatabase>}
 */
const getDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
};

/**
 * Generate cache key
 * @param {number} groupId - Group ID
 * @param {string} metricType - Type of metric
 * @param {number} version - Version number
 * @param {Object} options - Additional options (e.g., column_name)
 * @returns {string}
 */
export const generateCacheKey = (groupId, metricType, version, options = {}) => {
    let key = `group_${groupId}_${metricType}_v${version}`;
    
    if (options.column_name) {
        key += `_col_${options.column_name}`;
    }
    
    return key;
};

/**
 * Get cached data
 * @param {string} cacheKey - Cache key
 * @returns {Promise<Object|null>}
 */
export const getCachedData = async (cacheKey) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readonly');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        
        return new Promise((resolve, reject) => {
            const request = store.get(cacheKey);
            
            request.onsuccess = () => {
                const cached = request.result;
                
                if (!cached) {
                    resolve(null);
                    return;
                }
                
                // Check if expired
                if (Date.now() > cached.expires_at) {
                    console.log(`[Cache] Cache expired for key: ${cacheKey}`);
                    resolve(null);
                    return;
                }
                
                console.log(`[Cache] Cache hit for key: ${cacheKey}`);
                resolve(cached);
            };
            
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Cache] Error getting cached data:', error);
        return null;
    }
};

/**
 * Set cached data
 * @param {string} cacheKey - Cache key
 * @param {Object} data - Cache entry data
 * @returns {Promise<boolean>}
 */
export const setCachedData = async (cacheKey, data) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readwrite');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        
        const cacheEntry = {
            cache_key: cacheKey,
            group_id: data.group_id,
            metric_type: data.metric_type,
            version: data.version,
            data: data.data,
            calculated_at: Date.now(),
            expires_at: data.expires_at || (Date.now() + DEFAULT_CACHE_TTL),
            ...(data.column_name && { column_name: data.column_name })
        };
        
        return new Promise((resolve, reject) => {
            const request = store.put(cacheEntry);
            
            request.onsuccess = () => {
                console.log(`[Cache] Cache set for key: ${cacheKey}`);
                resolve(true);
            };
            
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Cache] Error setting cached data:', error);
        return false;
    }
};

/**
 * Invalidate cache for a specific group
 * @param {number} groupId - Group ID
 * @returns {Promise<number>} Number of entries invalidated
 */
export const invalidateGroupCache = async (groupId) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readwrite');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        const index = store.index('group_id');
        
        let deletedCount = 0;
        
        return new Promise((resolve, reject) => {
            const request = index.openCursor(IDBKeyRange.only(groupId));
            
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                
                if (cursor) {
                    cursor.delete();
                    deletedCount++;
                    cursor.continue();
                } else {
                    console.log(`[Cache] Invalidated ${deletedCount} cache entries for group ${groupId}`);
                    resolve(deletedCount);
                }
            };
            
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Cache] Error invalidating group cache:', error);
        return 0;
    }
};

/**
 * Invalidate cache by version
 * @param {number} groupId - Group ID
 * @param {number} oldVersion - Old version to invalidate
 * @returns {Promise<number>} Number of entries invalidated
 */
export const invalidateCacheByVersion = async (groupId, oldVersion) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readwrite');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        const index = store.index('group_id');
        
        let deletedCount = 0;
        
        return new Promise((resolve, reject) => {
            const request = index.openCursor(IDBKeyRange.only(groupId));
            
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                
                if (cursor) {
                    const entry = cursor.value;
                    if (entry.version < oldVersion) {
                        cursor.delete();
                        deletedCount++;
                    }
                    cursor.continue();
                } else {
                    console.log(`[Cache] Invalidated ${deletedCount} outdated cache entries`);
                    resolve(deletedCount);
                }
            };
            
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Cache] Error invalidating cache by version:', error);
        return 0;
    }
};

/**
 * Get all cached entries for a group
 * @param {number} groupId - Group ID
 * @returns {Promise<Array>}
 */
export const getGroupCacheEntries = async (groupId) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readonly');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        const index = store.index('group_id');
        
        return new Promise((resolve, reject) => {
            const request = index.getAll(IDBKeyRange.only(groupId));
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Cache] Error getting group cache entries:', error);
        return [];
    }
};

/**
 * Clear all cache
 * @returns {Promise<boolean>}
 */
export const clearAllCache = async () => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readwrite');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => {
                console.log('[Cache] All cache cleared');
                resolve(true);
            };
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Cache] Error clearing all cache:', error);
        return false;
    }
};

/**
 * Get cache statistics
 * @returns {Promise<Object>}
 */
export const getCacheStats = async () => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readonly');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        
        return new Promise((resolve, reject) => {
            const countRequest = store.count();
            
            countRequest.onsuccess = () => {
                const totalCount = countRequest.result;
                
                // Get expired count
                const index = store.index('expires_at');
                const expiredRequest = index.count(IDBKeyRange.upperBound(Date.now()));
                
                expiredRequest.onsuccess = () => {
                    resolve({
                        total: totalCount,
                        expired: expiredRequest.result,
                        active: totalCount - expiredRequest.result
                    });
                };
                
                expiredRequest.onerror = () => reject(expiredRequest.error);
            };
            
            countRequest.onerror = () => reject(countRequest.error);
        });
    } catch (error) {
        console.error('[Cache] Error getting cache stats:', error);
        return { total: 0, expired: 0, active: 0 };
    }
};
